import { Component, OnInit, OnDestroy, inject, signal, computed, ChangeDetectionStrategy, PLATFORM_ID } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { CommonModule, CurrencyPipe, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subscription, timer, throwError } from 'rxjs';
import { switchMap, takeWhile, catchError, tap, timeout, filter } from 'rxjs';
import { AlertService } from 'src/app/services/alert.service';
import { InfoTransactionService } from 'src/app/module-home/components/transactions/services/info-transaction.service';
import { InicializerService } from '@app/services/inicializer.service';
import { API_ROUTES } from '@utils/apiRoutes';
import { PixService } from '@app/services/pix.service';
import { environment } from '@environments/environment';
import {
  TransactionParamSchema,
  TransactionInfoResponseSchema,
  ReceiptDownloadResponseSchema,
  ReceiptEmailResponseSchema,
  PixQueueItemResponseSchema
} from '@app/schemas';

@Component({
  selector: 'app-transaction-status',
  templateUrl: './transaction-status.component.html',
  styleUrls: ['./transaction-status.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    CurrencyPipe,
    RouterModule
  ]
})
export class TransactionStatusComponent implements OnInit, OnDestroy {
  infoTransaction = signal<any>(null);
  isProcessingAction = signal(false);
  showOutHour = signal(false);
  showWeekend = signal(false);
  states = signal<{ [key: number]: string }>({ 10: 'Pendiente', 14: 'Aprobado', 12: 'Fallido', 9: 'Rechazado' });
  meansPay = signal<{ [key: string]: string }>({ 'pse': 'PSE', 'code': 'Código redimible', 'card': 'Tarjeta de crédito' });
  openModal = signal(true);
  messageStatusTransaction = signal<string>('Sin mensaje');
  showStatusTransaction = signal(false);
  statusBotPix = signal<boolean>(false);
  certificatesStatus = signal<any[]>([]);
  shouldDisplayPolling = signal<boolean>(false);
  transactionId = signal<string>('');

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private infoTransactionService = inject(InfoTransactionService);
  private alertService = inject(AlertService);
  private readonly inicializerSvc = inject(InicializerService);
  private pixService = inject(PixService);
  private platformId = inject(PLATFORM_ID);
  private longPollingSubscription?: Subscription;

  private statusPollAttempts = 0;
  private statusPollTimeout?: ReturnType<typeof setTimeout>;
  private pollStartTime = 0;
  private readonly MAX_STATUS_POLL_ATTEMPTS = 1;
  private readonly STATUS_POLL_INTERVAL_MS = 1500;
  private readonly MAX_TOTAL_WAIT_MS = 8000;
  private readonly RETRYABLE_STATUSES = [10];

  areAllReadyToDownload = computed(() => {
    const certs = this.certificatesStatus();
    if (!certs.length) return false;
    return certs.every(c => c.status === 'ready');
  });

  dataAlertSub?: Subscription;

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const rawId = this.route.snapshot.params['id'];
    const idTran = this.sanitizeTransactionId(rawId);
    this.transactionId.set(idTran);

    const paramValidation = TransactionParamSchema.safeParse({ idTransaction: idTran });
    if (!paramValidation.success) {
      console.warn('[TransactionStatus] Parámetro de transacción inválido:', paramValidation.error.format());
      this.openModal.set(false);
      this.router.navigate(['/']);
      return;
    }

    sessionStorage.removeItem('pse_redirected');
    sessionStorage.removeItem('pse_last_transaction_id');
    sessionStorage.removeItem('pse_certificates_backup');
    sessionStorage.removeItem('pse_form_backup');

    if (rawId && rawId !== idTran) {
      window.history.replaceState(null, '', `/estado-transaccion/${idTran}`);
    }

    this.getInfoTransaction(paramValidation.data.idTransaction);

    this.inicializerSvc.inicializeAlertActive();

    this.dataAlertSub = this.inicializerSvc.dataAlert$.pipe(
      filter((data: any) => data && Object.keys(data).length > 0)
    ).subscribe((data: any) => {
      if (data.show_status_transaction) {
        this.showStatusTransaction.set(true);
        this.messageStatusTransaction.set(data.message_status_transaction);
      }
      if (data.services_status?.PIXSERVICEPAY) {
        this.statusBotPix.set(true);
      }
    });
  }

  ngOnDestroy() {
    this.stopPolling();
    if (this.statusPollTimeout) {
      clearTimeout(this.statusPollTimeout);
    }
    if (this.dataAlertSub) {
      this.dataAlertSub.unsubscribe();
    }
  }

  getInfoTransaction(id: string) {
    if (this.statusPollAttempts === 0) {
      this.pollStartTime = Date.now();
    }
    this.openModal.set(true);

    this.infoTransactionService.getInfo(id).subscribe({
      next: (data: any) => {
        const elapsed = Date.now() - this.pollStartTime;
        const withinTimeBudget = elapsed + this.STATUS_POLL_INTERVAL_MS < this.MAX_TOTAL_WAIT_MS;

        const validation = TransactionInfoResponseSchema.safeParse(data);
        if (!validation.success) {
          console.warn('[TransactionStatus] El contrato de respuesta de transacción no se cumple:', validation.error.format());
        }

        if (data && data.request) {
          const status = data.request.id_pay_status;
          const esEstadoTransitorio = this.RETRYABLE_STATUSES.includes(status);
          const puedoReintentar = this.statusPollAttempts < this.MAX_STATUS_POLL_ATTEMPTS && withinTimeBudget;

          if (esEstadoTransitorio && puedoReintentar) {
            this.statusPollAttempts++;
            this.statusPollTimeout = setTimeout(
              () => this.getInfoTransaction(id),
              this.STATUS_POLL_INTERVAL_MS
            );
            return;
          }

          this.openModal.set(false);
          this.statusPollAttempts = 0;
          this.infoTransaction.set(data.request);
          this.outHour();

          if (this.infoTransaction().id_pay_status === 14 &&
            this.statusBotPix() &&
            (this.infoTransaction().id_state === 15 || this.infoTransaction().id_state === 11)) {
            const certificates = this.infoTransaction().certificates || [];
            const needsProcessing = certificates.some((cert: any) => cert.id_state !== 6);

            if (needsProcessing) {
              this.shouldDisplayPolling.set(true);
              this.initializeCertificatesState();
              this.startLongPolling();
            } else {
              this.shouldDisplayPolling.set(false);
            }
          }

        } else if (this.statusPollAttempts < this.MAX_STATUS_POLL_ATTEMPTS && withinTimeBudget) {
          this.statusPollAttempts++;
          this.statusPollTimeout = setTimeout(
            () => this.getInfoTransaction(id),
            this.STATUS_POLL_INTERVAL_MS
          );
        } else {
          this.openModal.set(false);
          this.statusPollAttempts = 0;
          this.router.navigate(['/']);
        }
      },
      error: (err) => {
        const elapsed = Date.now() - this.pollStartTime;
        const withinTimeBudget = elapsed + this.STATUS_POLL_INTERVAL_MS < this.MAX_TOTAL_WAIT_MS;

        if (this.statusPollAttempts < this.MAX_STATUS_POLL_ATTEMPTS && withinTimeBudget) {
          this.statusPollAttempts++;
          this.statusPollTimeout = setTimeout(
            () => this.getInfoTransaction(id),
            this.STATUS_POLL_INTERVAL_MS
          );
          return;
        }
        this.openModal.set(false);
        this.statusPollAttempts = 0;
        Sentry.captureException(err);
        this.alertService.alert('error', err.error?.err || 'Error al obtener la transacción');
        this.router.navigate(['/']);
      }
    });
  }

  private initializeCertificatesState(): void {
    const certificates = this.infoTransaction().certificates || [];
    const initialState = certificates.map((cert: any) => ({
      lookupId: cert.number,
      matricula: `${cert.office.description || 'Oficina'} ${cert.office.value}-${cert.number}`,
      pin: null,
      status: 'loading',
      data: null,
      errorMessage: null
    }));
    this.certificatesStatus.set(initialState);
  }

  private startLongPolling(): void {
    this.stopPolling();
    const reference = this.infoTransaction().id_request;
    if (!reference) return;

    const pollingInterval = 5000;
    const maxPollingTime = 1200000;

    this.longPollingSubscription = timer(0, pollingInterval).pipe(

      tap((index: number) => {
        if (index % 3 === 0) {
          this.openModal.set(true);
          setTimeout(() => {
            this.openModal.set(false);
          }, 2000);
        }
      }),

      switchMap(() =>
        this.pixService.getQueueItemByReference(reference).pipe(
          catchError(err => throwError(() => err))
        )
      ),

      tap((response: any) => {
        const queueValidation = PixQueueItemResponseSchema.safeParse(response);
        if (!queueValidation.success) {
          console.warn('[TransactionStatus] El contrato de respuesta de cola Pix no se cumple:', queueValidation.error.format());
        }
        if (response?.status === 'COMPLETED') {
          this.processFinalStatusResponse(response);
        }
        if (response?.status === 'PENDING_TIMEOUT') {
          this.certificatesStatus.update(certs => certs.map(c => ({ ...c, status: 'pending' })));
        }
      }),

      takeWhile((response: any) => response?.status !== 'COMPLETED', true),
      timeout({
        first: maxPollingTime,
        with: () => {
          this.openModal.set(false);
          this.certificatesStatus.update(certs => certs.map(c => ({ ...c, status: 'pending' })));
          return throwError(() => new Error('Polling timeout'));
        }
      }),

      catchError(err => {
        // Sentry.captureException(err);
        this.openModal.set(false);
        if (err.error?.status === 'FAILED') {
          this.processFailedStatusResponse(err.error);
        } else if (err.name !== 'TimeoutError') {
          this.certificatesStatus.update(certs => certs.map(c => ({ ...c, status: 'error' })));
        }
        return [];
      })
    ).subscribe();
  }

  private processFinalStatusResponse(response: any): void {
    if (response?.status === 'COMPLETED' && response.data) {
      this.updateCertificatesWithSuccessData(response.data);
    } else if (response?.status === 'PENDING_TIMEOUT') {
      this.certificatesStatus.update(certs => certs.map(c => ({ ...c, status: 'pending' })));
    } else {
      this.certificatesStatus.update(certs => certs.map(c => ({ ...c, status: 'error' })));
    }
  }

  private processFailedStatusResponse(response: any): void {
    const errorMessage = 'Hemos presentado inconvenientes en la generación del certificado. Estamos en proceso de revisión, en un momento recibirás más información al correo indicado.';

    this.certificatesStatus.update(currentCerts =>
      currentCerts.map(cert => ({
        ...cert,
        status: 'pending',
        errorMessage: errorMessage
      }))
    );
    this.stopPolling();
  }

  private updateCertificatesWithSuccessData(data: any): void {
    const finalCertificates = data.certificates;
    if (!finalCertificates || !Array.isArray(finalCertificates)) {
      this.certificatesStatus.update(certs => certs.map(c => ({ ...c, status: 'error' })));
      return;
    }
    this.certificatesStatus.update(currentCerts =>
      currentCerts.map(certInState => {
        const resultFromServer = finalCertificates.find((r: any) => r.number === certInState.lookupId);
        if (resultFromServer) {
          if (resultFromServer.URL) {
            return {
              ...certInState,
              status: 'ready',
              pin: resultFromServer.pinCertificateRC,
              data: resultFromServer,
            };
          }
          else if (resultFromServer.isVoucherSuccess) {
            return {
              ...certInState,
              status: 'voucher',
              pin: resultFromServer.voucherCode,
              data: resultFromServer,
            };
          }
        }
        return { ...certInState, status: 'error' };
      })
    );
  }

  private stopPolling(): void {
    if (this.longPollingSubscription) {
      this.longPollingSubscription.unsubscribe();
      this.longPollingSubscription = undefined;
    }
  }

  downloadCertificate(certificateData: any): void {
    if (!certificateData?.data?.URL) {
      this.alertService.alert('error', 'No se encontró la URL de descarga.');
      return;
    }
    window.open(certificateData.data.URL, '_blank');
  }

  async downloadAllCertificates(): Promise<void> {
    const readyCerts = this.certificatesStatus().filter(c => c.status === 'ready');
    if (readyCerts.length > 0) {
      readyCerts.forEach(cert => {
        if (cert?.data?.URL) {
          window.open(cert.data.URL, '_blank');
        }
      });
    }
  }

  showVoucherInstructions(): void {
    this.router.navigate(['/instrucciones-voucher']);
  }

  outHour() {
    let showMessage = false;
    const actualDay = new Date(this.infoTransaction().created_at);
    showMessage = actualDay.getHours() >= 19;
    this.showWeekend.set(actualDay.getDay() === 0 || actualDay.getDay() === 6);
    this.showOutHour.set(showMessage);
  }

  onRetry() {
    this.infoTransactionService.setInfoRequest(this.infoTransaction());
    this.router.navigate(['/proceso-pago']);
  }

  async downloadReceipt(): Promise<void> {
    if (this.isProcessingAction()) return;
    this.isProcessingAction.set(true);
    const id = this.infoTransaction().id_request;
    try {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

      if (isIOS) {
        window.open(`${environment.apiUrl + API_ROUTES.paymentProcess.routes.sendPDF}/${id}?action=stream`, "_blank");
      } else {
        const response: any = await this.infoTransactionService.downloadPdf(id).toPromise();
        const receiptValidation = ReceiptDownloadResponseSchema.safeParse(response);
        if (!receiptValidation.success) {
          console.warn('[TransactionStatus] El contrato de comprobante PDF no se cumple:', receiptValidation.error.format());
        }

        if (response && response.status === 200 && response.data && response.data.base64) {
          const byteCharacters = atob(response.data.base64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const fileName = response.data.nameFile;

          const downloadLink = document.createElement("a");
          downloadLink.href = url;
          downloadLink.download = fileName;
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
          window.URL.revokeObjectURL(url);

          this.alertService.alert('success', 'Comprobante generado correctamente.');
        } else {
          this.alertService.alert(
            'error',
            response?.message || 'No se pudo obtener el PDF en Base64 o la respuesta fue inválida.'
          );
        }
      }
    } catch (err: any) {
      Sentry.captureException(err);
      this.alertService.alert('error', err.error?.message || 'Error al descargar el comprobante.');
    } finally {
      this.isProcessingAction.set(false);
    }
  }

  async emailReceipt(): Promise<void> {
    if (this.isProcessingAction()) return;
    this.isProcessingAction.set(true);
    const id = this.infoTransaction().id_request;
    try {
      const response: any = await this.infoTransactionService.emailPdf(id).toPromise();
      const emailValidation = ReceiptEmailResponseSchema.safeParse(response);
      if (!emailValidation.success) {
        console.warn('[TransactionStatus] El contrato de envío de comprobante no se cumple:', emailValidation.error.format());
      }
      this.alertService.alert('success', 'Comprobante enviado exitosamente al correo.');
    } catch (err: any) {
      Sentry.captureException(err);
      this.alertService.alert('error', err.error?.message || 'Error al enviar el comprobante por correo.');
    } finally {
      this.isProcessingAction.set(false);
    }
  }

  recharge() {
    const id = this.transactionId() || this.sanitizeTransactionId(this.route.snapshot.params['id']);
    if (id) {
      window.location.href = `/estado-transaccion/${id}`;
    }
  }

  private sanitizeTransactionId(id: string | null | undefined): string {
    if (!id) return '';
    let cleanId = id;
    try {
      cleanId = decodeURIComponent(cleanId);
    } catch { }

    cleanId = cleanId.replace(/^\/+|\/+$/g, '').trim();

    if (cleanId.startsWith('$')) {
      cleanId = cleanId.slice(1);
    }
    cleanId = cleanId.split('$')[0];
    cleanId = cleanId.split('&')[0].split('?')[0].split('#')[0];
    cleanId = cleanId.replace(/^\/+|\/+$/g, '').trim();

    return cleanId;
  }
}